
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sprout, Star, Trash2, Calendar } from "lucide-react";
import { CropDetailsForm } from "@/components/CropDetailsForm";
import { ArchiveCropDialog } from "@/components/ArchiveCropDialog";
import { useCropPlan } from "@/hooks/useCropPlan";
import { usePosts } from "@/hooks/usePosts";
import { useToast } from "@/hooks/use-toast";
import { useAdaptiveSeasons } from "@/hooks/useAdaptiveSeasons";

interface CropPlan {
  id: string;
  commodity: string | null;
  variety?: string;
  season: string;
  year: number;
  group_name: string;
  is_primary: boolean;
}

interface CropPlanTabProps {
  cropPlans: CropPlan[];
  onRemoveCrop: (cropId: string, cropName: string) => void;
  isEditing?: boolean;
}

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear + i);

export const CropPlanTab = ({ cropPlans, onRemoveCrop, isEditing = false }: CropPlanTabProps) => {
  const { seasons, seasonType } = useAdaptiveSeasons();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedSeason, setSelectedSeason] = useState<string>("");
  const [cropDetailsData, setCropDetailsData] = useState<any>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [viewYear, setViewYear] = useState(currentYear);
  const [archiveDialog, setArchiveDialog] = useState<{
    isOpen: boolean;
    cropId: string;
    cropName: string;
  }>({ isOpen: false, cropId: "", cropName: "" });

  const { addCropToPlan, loadUserCropPlans } = useCropPlan();
  const { posts } = usePosts();
  const { toast } = useToast();

  const handleCropDetailsSave = async (data: any) => {
    console.log("🌱 Crop details saved in CropPlanTab:", data);
    setCropDetailsData(data);
    
    // If we have season and year selected, automatically add to plan
    if (selectedYear && selectedSeason && data.crop && data.cropGroup) {
      await handleAddToPlan(data);
    }
  };

  const handleAddToPlan = async (data = cropDetailsData) => {
    if (!selectedYear || !selectedSeason || !data?.crop || !data?.cropGroup) {
      toast({
        title: "Missing Information",
        description: "Please select year, season, and fill in crop details before adding",
        variant: "destructive",
      });
      return;
    }

    console.log("Adding crop to plan:", { 
      selectedYear, 
      selectedSeason, 
      cropDetailsData: data
    });
    
    setIsAdding(true);
    
    try {
      const success = await addCropToPlan(
        selectedYear,
        selectedSeason,
        data.crop,
        data.cropVariety || "Standard",
        data.cropGroup
      );

      console.log("Add crop result:", success);

      if (success) {
        toast({
          title: "Success",
          description: `${data.crop} added to your ${selectedSeason} ${selectedYear} plan`,
        });
        
        // Force reload the crop plans to ensure the UI updates
        console.log("Force reloading crop plans...");
        await loadUserCropPlans();
        
        // Reset form
        setSelectedSeason("");
        setCropDetailsData(null);
      } else {
        toast({
          title: "Error", 
          description: "Failed to add crop to plan. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error adding crop:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveClick = (cropId: string, cropName: string) => {
    setArchiveDialog({ isOpen: true, cropId, cropName });
  };

  const handleConfirmRemove = () => {
    onRemoveCrop(archiveDialog.cropId, archiveDialog.cropName);
  };

  const getRelatedPosts = (cropName: string) => {
    return posts.filter(post => 
      post.crop?.toLowerCase().includes(cropName.toLowerCase()) ||
      post.content.toLowerCase().includes(cropName.toLowerCase())
    );
  };

  // Filter crops by the selected view year
  const currentYearCrops = cropPlans.filter(crop => crop.year === viewYear);

  // Group crops by season for the selected year
  const groupCropsBySeason = () => {
    const grouped: { [season: string]: CropPlan[] } = {};
    
    currentYearCrops.forEach(crop => {
      if (!grouped[crop.season]) {
        grouped[crop.season] = [];
      }
      grouped[crop.season].push(crop);
    });
    
    return grouped;
  };

  // Get available years from crop plans
  const availableYears = [...new Set(cropPlans.map(crop => crop.year))].sort((a, b) => b - a);

  // Also reload when the component mounts or when cropPlans prop changes
  useEffect(() => {
    console.log("CropPlanTab: cropPlans updated, count:", cropPlans.length);
  }, [cropPlans]);

  const isButtonDisabled = !selectedYear || !selectedSeason || !cropDetailsData?.crop || !cropDetailsData?.cropGroup || isAdding;
  const groupedCrops = groupCropsBySeason();

  return (
    <div className="space-y-6">
      {/* Header with year selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-green-800">
          My Crop Plan ({currentYearCrops.length} crops for {viewYear})
        </h3>
        {availableYears.length > 1 && (
          <Select value={viewYear.toString()} onValueChange={(value) => setViewYear(parseInt(value))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      
      {/* Add New Crop Section - Only show in edit mode */}
      {isEditing && (
        <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
          <h4 className="font-medium text-green-800">Add New Crop to Plan</h4>
          
          {/* Planning Details */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Year</label>
              <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Season</label>
              <Select value={selectedSeason} onValueChange={setSelectedSeason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select season" />
                </SelectTrigger>
                <SelectContent>
                  {seasons.map(season => (
                    <SelectItem key={season} value={season}>{season}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Crop Details Form */}
          <CropDetailsForm onSave={handleCropDetailsSave} />

          {/* Add to Plan Button */}
          {cropDetailsData && (
            <Button 
              onClick={() => handleAddToPlan()}
              disabled={isButtonDisabled}
              className="w-full"
            >
              {isAdding ? "Adding..." : `Add ${cropDetailsData.crop} to ${selectedSeason} ${selectedYear} Plan`}
            </Button>
          )}
        </div>
      )}
      
      {/* Current Year Crops Display */}
      {currentYearCrops.length === 0 ? (
        <div className="text-center py-8">
          <Sprout className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">No crops planned for {viewYear}</p>
          {isEditing ? (
            <p className="text-sm text-gray-400">Add some crops using the form above to get started!</p>
          ) : (
            <p className="text-sm text-gray-400">Switch to edit mode to add crops to your plan.</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 border-b border-green-200 pb-2">
            <Calendar className="h-5 w-5 text-green-600" />
            <h4 className="text-lg font-semibold text-green-800">{viewYear} Crop Plan</h4>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {seasons
              .filter(season => groupedCrops[season]?.length > 0)
              .map(season => (
                <div key={season} className="space-y-3">
                  <div className="bg-green-100 px-3 py-2 rounded-md">
                    <h5 className="font-medium text-green-800 text-sm">{season}</h5>
                  </div>
                  
                  <div className="space-y-2">
                    {groupedCrops[season].map(crop => (
                      <div key={crop.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center space-x-2">
                          {crop.is_primary && (
                            <Star className="text-yellow-500 fill-yellow-500" size={14} />
                          )}
                          <div>
                            <div className="font-medium text-gray-900 text-sm">
                              {crop.commodity} {crop.variety && `- ${crop.variety}`}
                            </div>
                            <div className="text-xs text-gray-500">
                              {crop.group_name}
                            </div>
                          </div>
                        </div>
                        {isEditing && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveClick(crop.id, crop.commodity || 'Crop')}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 h-8 w-8 p-0"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Archive Dialog */}
      <ArchiveCropDialog
        isOpen={archiveDialog.isOpen}
        onClose={() => setArchiveDialog({ isOpen: false, cropId: "", cropName: "" })}
        cropName={archiveDialog.cropName}
        cropId={archiveDialog.cropId}
        relatedPosts={getRelatedPosts(archiveDialog.cropName)}
        onConfirmRemove={handleConfirmRemove}
      />
    </div>
  );
};
